import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AdverseEventData } from '@/components/ClinicalAssement/types';
import { adverseEventService } from '@/services/adverseEventService';
import { RootState } from '../store';

const DEFAULT_VALUE_UNKNOWN = "Unknown";
interface AdverseEventState {
  data: AdverseEventData | null;
  pendingChanges: {
    eventInformation: Partial<AdverseEventData>;
    triage: Partial<AdverseEventData>;
    assessment: Partial<AdverseEventData>;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: AdverseEventState = {
  data: null,
  pendingChanges: {
    eventInformation: {},
    triage: {},
    assessment: {}
  },
  isLoading: false,
  error: null
};

// Async thunks
export const addAdverseEvent = createAsyncThunk(
  'adverseEvent/add',
  async (data: Partial<AdverseEventData>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const existingData = state.adverseEvent.data || {} as Partial<AdverseEventData>;
      const mergedData = { ...existingData, ...data, id: 0 };
      const response = await adverseEventService.addAdverseEvent(mergedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add adverse event');
    }
  }
);

export const updateAdverseEvent = createAsyncThunk(
  'adverseEvent/update',
  async ({ caseId, data }: { caseId: number; data: Partial<AdverseEventData> }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const existingData = state.adverseEvent.data || {} as Partial<AdverseEventData>;
      const mergedData = { ...existingData, ...data, case_id: caseId, id: existingData.id || 0 };
      const response = await adverseEventService.updateAdverseEvent(caseId, mergedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update adverse event');
    }
  }
);

export const fetchAdverseEvent = createAsyncThunk(
  'adverseEvent/fetch',
  async (caseId: number, { rejectWithValue }) => {
    try {
      const response = await adverseEventService.getAdverseEvent(caseId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch adverse event');
    }
  }
);

export const adverseEventSlice = createSlice({
  name: 'adverseEvent',
  initialState,
  reducers: {
    setAdverseEventData: (state, action: PayloadAction<AdverseEventData>) => {
      state.data = action.payload;
      // Clear pending changes when new data is loaded
      state.pendingChanges = {
        eventInformation: {},
        triage: {},
        assessment: {}
      };
    },
    updateEventInformationField: (
      state,
      action: PayloadAction<{ field: keyof AdverseEventData; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.pendingChanges.eventInformation[field] = value;
    },
    updateTriageField: (
      state,
      action: PayloadAction<{ field: keyof AdverseEventData; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.pendingChanges.triage[field] = value;
    },
    updateAssessmentField: (
      state,
      action: PayloadAction<{ field: keyof AdverseEventData; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.pendingChanges.assessment[field] = value;
    },
    clearPendingChanges: (
      state,
      action: PayloadAction<'eventInformation' | 'triage' | 'assessment'>
    ) => {
      state.pendingChanges[action.payload] = {};
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add Adverse Event
      .addCase(addAdverseEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addAdverseEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.pendingChanges = {
          eventInformation: {},
          triage: {},
          assessment: {}
        };
      })
      .addCase(addAdverseEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Adverse Event
      .addCase(updateAdverseEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAdverseEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.pendingChanges = {
          eventInformation: {},
          triage: {},
          assessment: {}
        };
      })
      .addCase(updateAdverseEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Adverse Event
      .addCase(fetchAdverseEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdverseEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.pendingChanges = {
          eventInformation: {},
          triage: {},
          assessment: {}
        };
      })
      .addCase(fetchAdverseEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setAdverseEventData,
  updateEventInformationField,
  updateTriageField,
  updateAssessmentField,
  clearPendingChanges,
  setLoading,
  setError
} = adverseEventSlice.actions;

export default adverseEventSlice.reducer; 